import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect, useState } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { PackageOrderSummary } from "@/features/Packages/PackageOrderSummary";
import { PaymentMethod } from "@/features/Square/PaymentMethod";
import { SelectPaymentModal } from "@/features/Square/SelectPaymentModal";
import { useCreateUserCardOnFile } from "@/features/Square/SquareCardEntryModal/hooks/useCreateUserCardOnFile";
import { Loader } from "@whoosh/design-system/src/components/display";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { VStack } from "@whoosh/design-system/src/components/primitive";
import { useAssignPaymentToPackage } from "@whoosh/features/src/Packages";
import { SquareCard } from "@whoosh/features/src/Square/features/SquareCardEntry/types";

import { PackageOrderQuery } from "./__generated__/PackageOrderQuery.graphql";

type Props = {
  orderId: string;
  packageId: string;
};

export const PackageOrder = ({ orderId, packageId }: Props) => {
  const [queryRef, loadQuery] = useQueryLoader<PackageOrderQuery>(
    PackageOrderQueryString
  );

  useEffect(() => {
    if (orderId && packageId) {
      loadQuery({ orderId, packageId });
    }
  }, [loadQuery, orderId, packageId]);

  return (
    <Suspense fallback={<Loader />}>
      {queryRef ? <PackageOrderInner queryRef={queryRef} /> : null}
    </Suspense>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<PackageOrderQuery>;
};

const PackageOrderInner = ({ queryRef }: InnerProps) => {
  const { showToast } = useToast();
  const [isPaymentMethodSelectModalOpen, setIsPaymentMethodSelectModalOpen] =
    useState(false);

  const data = usePreloadedQuery(PackageOrderQueryString, queryRef);

  const [createSquareCard] = useCreateUserCardOnFile();
  const [assignPaymentMethodToPackage] = useAssignPaymentToPackage();

  const memberPackage = data.memberPackage;
  const customerId = memberPackage?.customer?.id || "";
  const packageId = memberPackage?.id || "";

  const handlePaymentMethodSelected = (paymentMethodId: string) => {
    if (memberPackage) {
      assignPaymentMethodToPackage(
        { id: packageId, squareCardId: paymentMethodId },
        () => {
          setIsPaymentMethodSelectModalOpen(false);
        },
        (error: any) => {
          setTimeout(() => {
            showToast({
              title: t`Error`,
              description: `${t`Unable to add payment method`} - ${
                error.message
              }`,
              semanticStyle: "error",
            });
          }, 10);
        }
      );
    }
  };

  const handleAddNewCard = (squareCard: SquareCard): Promise<string> =>
    createSquareCard({ squareNonce: squareCard.nonce });
  return (
    <>
      <VStack px={6} gap={8}>
        <PackageOrderSummary orderRef={data.order} />
        <PaymentMethod
          squareCardRef={data.memberPackage?.squareCard}
          onAddPress={() => {
            setIsPaymentMethodSelectModalOpen(true);
          }}
          onChangePress={() => {
            setIsPaymentMethodSelectModalOpen(true);
          }}
        />
      </VStack>
      {isPaymentMethodSelectModalOpen ? (
        <SelectPaymentModal
          customerId={customerId}
          isOpen={isPaymentMethodSelectModalOpen}
          onClose={() => setIsPaymentMethodSelectModalOpen(false)}
          onAddNewCard={handleAddNewCard}
          onSelectPaymentMethod={handlePaymentMethodSelected}
        />
      ) : null}
    </>
  );
};

const PackageOrderQueryString = graphql`
  query PackageOrderQuery($orderId: ID!, $packageId: ID!) {
    memberPackage: node(id: $packageId) {
      ... on MemberPackage {
        id
        customer {
          id
        }
        squareCard {
          ...PaymentMethod_squareCardOnFile
        }
      }
    }
    order: node(id: $orderId) {
      ... on Order {
        ...PackageOrderSummary_order
      }
    }
  }
`;
